import React from "react";
import {
  Button,
  CollateralTable,
  DropDown,
  EventLogTable,
  OrderTable,
  TextInput,
} from "./components";
import { useVirtualOMS } from "./hook";
import { Refresh } from "@mui/icons-material";
import DynamicDropdown from "./components/DropDown/DynamicDropdown";
import { getUsers } from "src/Requests/connections";
import { FullScreenLoading } from "src/Components";

const VirtualOMS: React.FC = () => {
  const {
    accounts,
    balance,
    mainBuyPower,
    optionBuyPower,
    credit,
    optionCredit,
    vajhTazmin,
    user,
    orders,
    collateralStrategies,
    eventLogs,
    activeTab,
    setActiveTab,
    setUser,
    handleAddRemainingBalance,
    handleBalanceChange,
    handleSetBuyingPowers,
    setMainBuyPower,
    setOptionBuyPower,
    setCredit,
    setOptionCredit,
    setVajhTazmin,
    handleResetAccount,
    setResetFlag,
    clonePorfoAccount,
    handleClearEventLogs,
    handleClearCollateralStrategies,
    handleCancelOpenOrders,
    loading,
    cloneSource,
    setCloneSource,
  } = useVirtualOMS();

  const tabs = [
    { id: "OPENOrder", label: "Open Orders" },
    { id: "TradeOrder", label: "Trade Orders" },
    { id: "Collateral", label: "Collateral" },
    { id: "EventLog", label: "Event Log" },
  ] as const;

  return (
    <div className="m-6 flex flex-col gap-5">
      {loading && <FullScreenLoading />}

      <section className="grid w-full grid-cols-1 gap-5 rounded-2xl border border-slate-200 bg-slate-50 p-5 shadow-sm xl:grid-cols-12">
        <div className="flex flex-col gap-4 xl:col-span-3">
          <div>
            <h2 className="text-base font-semibold text-slate-900">Account</h2>
            <p className="mt-1 text-sm text-slate-500">Select and manage the virtual account.</p>
          </div>
          <DropDown
            placeholder="Select account"
            options={accounts}
            selectedOption={user}
            onSelectedOptionChange={setUser}
          />
          <TextInput
            label="Balance"
            value={balance}
            onChange={handleBalanceChange}
            type="price"
          />
        </div>

        <div className="xl:col-span-6">
          <div className="mb-4">
            <h2 className="text-base font-semibold text-slate-900">Buying powers</h2>
            <p className="mt-1 text-sm text-slate-500">Values returned by the Virtual OMS portfolio API.</p>
          </div>
          <div className="grid grid-cols-1 gap-x-4 sm:grid-cols-2 xl:grid-cols-5">
            <TextInput label="Main Buy Power" value={mainBuyPower} onChange={(value) => setMainBuyPower(Number(value))} type="price" />
            <TextInput label="Option Buy Power" value={optionBuyPower} onChange={(value) => setOptionBuyPower(Number(value))} type="price" />
            <TextInput label="Credit" value={credit} onChange={(value) => setCredit(Number(value))} type="price" />
            <TextInput label="Option Credit" value={optionCredit} onChange={(value) => setOptionCredit(Number(value))} type="price" />
            <TextInput label="وجه تضمین اختیار معامله" value={vajhTazmin} onChange={(value) => setVajhTazmin(Number(value))} type="price" />
          </div>
        </div>

        <div className="flex flex-col gap-4 xl:col-span-3">
          <div>
            <h2 className="text-base font-semibold text-slate-900">Clone account</h2>
            <p className="mt-1 text-sm text-slate-500">Copy portfolio and account values from a source account.</p>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <DynamicDropdown
              fetchData={getUsers}
              placeholder="Source Account"
              selectedOption={{ id: cloneSource.id, name: cloneSource.name }}
              onSelectedOptionChange={(value) => setCloneSource(value)}
            />
          </div>
        </div>

        <div className="flex flex-wrap justify-end gap-2 border-t border-slate-200 pt-4 xl:col-span-12">
          <Button label="Set Balance" onClick={handleAddRemainingBalance} />
          <Button label="Reset Account" onClick={handleResetAccount} disabled={!user} />
          <Button label="Save Buying Powers" onClick={handleSetBuyingPowers} disabled={!user} />
          <Button label="Clone" onClick={clonePorfoAccount} />
        </div>
      </section>

      <div className="w-full">
        <div className="mb-3 flex items-center gap-3">
          <div className="grid flex-1 grid-cols-2 gap-2 md:grid-cols-4">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`rounded-xl px-4 py-3 text-sm font-semibold transition ${
                  activeTab === tab.id
                    ? "bg-slate-700 text-white shadow"
                    : "bg-slate-200 text-slate-800 hover:bg-slate-300"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <button
            type="button"
            className="rounded-lg p-2 text-slate-700 hover:bg-slate-100"
            onClick={() => setResetFlag(true)}
            aria-label="Refresh active tab"
          >
            <Refresh />
          </button>
        </div>

        <div className="relative min-h-[180px] w-full rounded-2xl bg-white p-6 text-black shadow-lg">
          {activeTab === "OPENOrder" && (
            <>
              <div className="mb-4 flex justify-end">
                <Button label="Cancel Open Orders" onClick={handleCancelOpenOrders} />
              </div>
              <OrderTable isTrade={false} rows={orders} onAction={() => setResetFlag(true)} />
            </>
          )}

          {activeTab === "TradeOrder" && <OrderTable rows={orders} isTrade onAction={() => {}} />}

          {activeTab === "Collateral" && (
            <>
              <div className="mb-4 flex justify-end">
                <Button label="Clear Collateral" onClick={handleClearCollateralStrategies} />
              </div>
              <CollateralTable rows={collateralStrategies} />
            </>
          )}

          {activeTab === "EventLog" && (
            <>
              <div className="mb-4 flex justify-end">
                <Button label="Clear Event Log" onClick={handleClearEventLogs} />
              </div>
              <EventLogTable rows={eventLogs} />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default VirtualOMS;
